import React from 'react';
import { Autocomplete, Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  ApiKeyDataScope,
  ApiKeyScopeOptions,
  ApiKeyScopePresetId,
  dataTypeLabel,
} from '../types/apiKeyDataScope';

interface ApiKeyDataScopePickerProps {
  /** null = the key inherits its organization scope (nothing stored). */
  value: ApiKeyDataScope | null;
  onChange: (scope: ApiKeyDataScope | null) => void;
  /** From GET /api-keys/scope-options; nothing renders until it says the flag is on. */
  options: ApiKeyScopeOptions | null;
  disabled?: boolean;
}

const INHERIT = 'inherit';

/**
 * The scope to emit when a preset is chosen. `full` carries no limits (its
 * pickers are hidden, so leftovers could not be cleared); a restricted preset
 * keeps brands and product types but owns the data types; only `custom` keeps a
 * data types list.
 */
export function nextScopeForPreset(value: ApiKeyDataScope | null, id: string): ApiKeyDataScope | null {
  if (id === INHERIT) return null;
  const next: ApiKeyDataScope = { preset: id as ApiKeyScopePresetId };
  if (id === 'full') return next;
  if (value?.brands?.length) next.brands = value.brands;
  if (value?.productTypes?.length) next.productTypes = value.productTypes;
  if (id === 'custom' && value?.dataTypes?.length) next.dataTypes = value.dataTypes;
  return next;
}

/**
 * Data scope for one API key: a named preset, or custom brands / data types /
 * product types. Choices only narrow what the organization can already see.
 */
export const ApiKeyDataScopePicker: React.FC<ApiKeyDataScopePickerProps> = ({ value, onChange, options, disabled }) => {
  if (!options || !options.enabled) return null;

  const selected: string = value ? value.preset || 'custom' : INHERIT;
  const preset = options.presets.find((p) => p.id === selected);
  const isCustom = selected === 'custom';
  const showLimits = value !== null && selected !== 'full';

  const setPreset = (id: string) => onChange(nextScopeForPreset(value, id));
  const patch = (changes: Partial<ApiKeyDataScope>) => onChange({ ...(value || { preset: 'custom' }), ...changes });

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Data scope
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        label="Profile"
        value={selected}
        onChange={(e) => setPreset(e.target.value)}
        disabled={disabled}
        helperText={
          preset?.description ||
          (selected === INHERIT ? 'The key sees everything your organization can see.' : undefined)
        }
      >
        <MenuItem value={INHERIT}>Inherit organization scope</MenuItem>
        {options.presets.map((p) => (
          <MenuItem key={p.id} value={p.id} disabled={!p.allowed}>
            {p.label}
            {!p.allowed ? ' (needs the Products API plan)' : ''}
          </MenuItem>
        ))}
      </TextField>

      {showLimits && (
        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
          <Autocomplete
            multiple
            size="small"
            options={options.brands}
            value={value?.brands || []}
            onChange={(_, brands) => patch({ brands })}
            disabled={disabled}
            renderInput={(params) => (
              <TextField {...params} label="Brands" helperText="Empty means every brand your organization has." />
            )}
          />
          {isCustom && (
            <Autocomplete
              multiple
              size="small"
              options={options.dataTypes}
              getOptionLabel={dataTypeLabel}
              value={value?.dataTypes || []}
              onChange={(_, dataTypes) => patch({ dataTypes })}
              disabled={disabled}
              renderInput={(params) => (
                <TextField {...params} label="Data types" helperText="Empty means every data type." />
              )}
            />
          )}
          <Autocomplete
            multiple
            size="small"
            options={options.productTypes}
            value={value?.productTypes || []}
            onChange={(_, productTypes) => patch({ productTypes })}
            disabled={disabled}
            renderInput={(params) => (
              <TextField {...params} label="Product types" helperText="Empty means cameras, lenses and accessories." />
            )}
          />
        </Box>
      )}
    </Box>
  );
};
